import { PLAN } from '../constants';
// import { getUniqueEntriesFromArray } from '../helpers/utilities';
import { yachtCardFromStandardYachtWeek } from '../helpers/yachts';
import { isStandardBooking } from '../helpers/booking';
import { getTotalPrice } from '../helpers/pricing';
import { maxCapacityCrew } from '../helpers/crew';

export const initialState = {
  plan: undefined,
  loading: false,
  error: undefined,
  weeks: undefined,
  destinations: undefined,
  pricing: undefined,
  available: {
    boat: true,
    week: true
  },
  bestWeeks: [],
  comments: [],
  isCrew: undefined,
  standardYacht: undefined,
  sameYachtPlanNum: undefined,
  yachts: [],
  filtered: []
};

// We're only including weeks for the ongoing or upcoming season in the planner but not both
// Examples:
// Plans in the middle of 2019 summer season will see 2019 weeks and yachts for Croatia and 2020 for BVI
// Plans after the 2019 summer season has finished will see 2020 weeks for all destinations
// Note: commenting out for now to see if it breaks anything
const filterWeeksForCurrentYearPerDestination = weeks => {
  return weeks;
  // const destinations = getUniqueEntriesFromArray(weeks.map(w => w.destination.id));
  // const yearForDestination = {};
  // destinations.forEach(d => {
  //   yearForDestination[d] = Math.min(
  //     ...weeks
  //       .filter(w => w.destination.id === d)
  //       .map(w => w.year)
  //   );
  // });
  // return weeks.filter(w => w.year === yearForDestination[w.destination.id]);
};

const filterYachtsToCurrentWeeks = (yachts, weeks) => (
  yachts.filter(y => weeks.some(w => (
    w.destination.id === y.week.destinationId &&
    w.week === y.week.week &&
    w.year === y.week.year
  )))
);

// Filter planner yachts based on filter criteria (simplified version without destination/crew filtering)
export const filterPlannerYachts = (state, payload) => {
  const {
    hullType = [],
    amenities = [],
    marina = [],
    partners = [],
    capacity = {},
    boatYear = [],
    boatCabins = [],
    boatSize = [],
    priceRange = {},
    weekFilter = [],
    countryFilter = [],
    festivalFilter = []
  } = payload;

  let { yachtTypes = [] } = payload;
  yachtTypes = yachtTypes.map(type => parseInt(type, 10));
  return (state.yachts || []).filter(item => {
    // Capacity filtering
    if (capacity.minCapacity && capacity.minCapacity > parseInt(item.boat.capacity - 1, 10)) {
      return false;
    }
    if (capacity.maxCapacity && capacity.maxCapacity < parseInt(item.boat.capacity - 1, 10)) {
      return false;
    }

    // Yacht type filtering
    if (yachtTypes.length > 0 && yachtTypes.indexOf(item.boat.ratingId) === -1) {
      return false;
    }

    // Marina filtering
    if (marina.length > 0 && marina.indexOf(item.marina.name) === -1) {
      return false;
    }

    // Hull type filtering
    if (hullType.length > 0 && hullType.indexOf(item.boat.hullType) === -1) {
      return false;
    }

    // Cabins filtering
    if (boatCabins.length > 0 && boatCabins.indexOf(`${item.boat.cabins}`) === -1) {
      return false;
    }

    // Boat size filtering
    if (boatSize.length > 0 && boatSize.indexOf(`${item.boat.size}`) === -1) {
      return false;
    }

    // Boat year filtering
    if (boatYear.length > 0 && boatYear.indexOf(`${item.boat.year}`) === -1) {
      return false;
    }

    // Air conditioning filtering
    const hasAC = item.boat.attributes.indexOf(1) !== -1 && item.boat.attributes.indexOf(2) !== -1;
    if (amenities.length > 0 && amenities.indexOf('Air Conditioning') !== -1 && !hasAC) {
      return false;
    }

    // Partners filtering
    if (partners.length && !partners.includes(item.boat.partner.id)) {
      return false;
    }

    // Week filtering
    if (weekFilter.length && !weekFilter.includes(`${item.week.week}`)) {
      return false;
    }

    // Festival filtering
    if (festivalFilter.length && !festivalFilter.includes(`${item.week.id}`)) {
      return false;
    }
    // console.log({ countryFilter, boatCountry: item.week.destinationCountry, item });
    // Country filtering
    if (countryFilter.length && !countryFilter.includes(`${item.week.destinationCountry}`)) {
      return false;
    }

    // Price range filtering
    if (priceRange.min !== undefined && priceRange.max !== undefined) {
      const totalPrice = getTotalPrice(item, maxCapacityCrew(item.boat.capacity));
      if (totalPrice > priceRange.max || totalPrice < priceRange.min) {
        return false;
      }
    }

    return true;
  });
};

const planner = (state = initialState, action) => {
  switch (action.type) {
  case 'UPDATE_PLAN_BOOKING':
    return {
      ...state,
      plan: action.booking,
      comments: action.comments,
      standardYacht: (isStandardBooking(action.booking)) ? yachtCardFromStandardYachtWeek(
        action.booking.standardYachtWeek,
        action.booking.week
        // state.weeks.find(w => w.week === action.booking.week.week && w.year === action.booking.week.year)
      ) : null,
      bestWeeks: action.bestWeeks,
      available: action.available,
      sameYachtPlanNum: action.sameYachtPlanNum
    };
  case 'UPDATE_PRICING':
    return {
      ...state,
      pricing: action.pricing
    };
  case 'UPDATE_WEEKS':
    return {
      ...state,
      weeks: filterWeeksForCurrentYearPerDestination(action.weeks)
    };
  case 'UPDATE_YACHTS':
    return {
      ...state,
      yachts: filterYachtsToCurrentWeeks(action.yachts, state.weeks)
    };
  case 'FILTER_PLANNER_YACHTS':
    return {
      ...state,
      filtered: filterPlannerYachts(state, action.payload)
    };
  case 'UPDATE_WEEK_VOTE': {
    const newBooking = { ...state.plan };
    if (!newBooking.crewMembers) {
      return null;
    }
    const crewIndex = newBooking.crewMembers.findIndex(member => member.id === action.crewMember.id);
    newBooking.crewMembers[crewIndex] = action.crewMember;
    return {
      ...state,
      plan: newBooking,
      bestWeeks: action.bestWeeks
    };
  }
  case 'UPDATE_CREW_RATIO': {
    const newPlan = {
      ...state.plan,
      numBoys: action.numBoys,
      numGirls: action.numGirls
    };
    return {
      ...state,
      plan: newPlan
    };
  }
  case 'UPDATE_PERSONNEL': {
    const newState = {
      ...state,
      plan: {
        ...state.plan,
        needHostess: !!action.hostess
      }
    };
    if (state.pricing) {
      newState.pricing = {
        ...state.pricing,
        [PLAN.PRICING.HOST]: {
          ...state.pricing[PLAN.PRICING.HOST],
          selected: !!action[PLAN.PRICING.HOST]
        }
      };
    }
    return newState;
  }
  case 'ADD_COMMENT': {
    return {
      ...state,
      comments: [action.comment, ...state.comments]
    };
  }
  case 'UPDATE_TITLE': {
    return {
      ...state,
      title: action.title
    };
  }
  case 'PLAN_INVITE_EMAILS':
    // @TODO: add crew member
    return {
      ...state
    };
  case 'UPDATE_CREW_MEMBER': {
    const newBooking = { ...state.plan };
    const newCrewMembers = [];
    state.plan.crewMembers.forEach(member => {
      if (member.id !== action.crewMember.id) {
        newCrewMembers[member.id] = { ...member };
      } else {
        newCrewMembers[member.id] = { ...member, status: action.status };
      }
    });
    newBooking.crewMembers = newCrewMembers;
    return {
      ...state,
      plan: newBooking
    };
  }
  case 'UPDATE_LEAD_BOOKER': {
    const newPlan = { ...state.plan };
    newPlan.booker = action.booker.profile;
    return {
      ...state,
      plan: newPlan
    };
  }
  case 'CANCEL_PLAN': {
    return {
      ...state,
      activePlans: action.activePlans
    };
  }
  case 'UPDATE_PLAN': {
    return {
      ...state,
      plan: action.booking,
      standardYacht: action.standardYacht,
      available: initialState.available
    };
  }
  case 'UPDATE_DESTINATION': {
    return {
      ...state,
      plan: action.booking,
      standardYacht: undefined
    };
  }
  case 'BOOK_PLAN': {
    return {
      ...state
    };
  }
  case 'CREATE_PLAN': {
    return {
      ...state,
      activePlans: [...(state.activePlans || []), action.booking],
      plan: action.booking,
      standardYacht: action.standardYacht,
      isCrew: true,
      weeks: state.weeks,
      yachts: state.yachts
    };
  }
  case 'UPDATE_ACTIVE_PLANS': {
    return {
      ...state,
      activePlans: action.activePlans
    };
  }
  case 'UPDATE_IS_CREW': {
    return {
      ...state,
      isCrew: action.isCrew
    };
  }
  case 'UPDATE_JOIN_DATA': {
    return {
      ...state,
      joinData: action.data
    };
  }
  default:
    return state;
  }
};

export default planner;
